import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";
import { callAPI } from "../../helper/apiUtils";
import { BASE_URL } from "../../helper/constants";
import LanguageContext from "../../helper/LanguageContext";
import CompanyDocumentViewerModal from "../../components/CompanyDocumentViewerModal";
import { HiDownload, HiOutlineEye } from "react-icons/hi";
import { useSelector } from "react-redux";
import "./CompanyDocuments.css";

export const INTERN_DOCS_COMPANY_KEY = "kjuup_intern_docs_company_id";

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function extractDocs(data) {
  if (!data) return [];
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.documents)) return data.documents;
  if (Array.isArray(data)) return data;
  return [];
}

async function fetchDocs(companyId) {
  const { data } = await callAPI(`/company/${companyId}/document`, {}, "GET");
  return extractDocs(data);
}

function CompanyDocuments(props) {
  const { strings } = useContext(LanguageContext);
  const routeCompanyId = (props.location?.pathname || "").match(/^\/company-documents\/([^/]+)/)?.[1] || null;
  const selectedCompanyId = useSelector((state) => state.selectedCompanyId);
  const isDesktop = useSelector((state) => state.isDeskTopResolution);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDoc, setViewDoc] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const loadedCompanyRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let internId = null;
      try {
        internId = sessionStorage.getItem(INTERN_DOCS_COMPANY_KEY);
      } catch (_) {
        internId = null;
      }

      const ordered = [];
      const add = (id) => {
        if (id && !ordered.includes(id)) ordered.push(id);
      };
      add(internId);
      add(routeCompanyId);
      add(selectedCompanyId);

      const tryIds = async (ids) => {
        let docs = [];
        let usedId = ids[0] || routeCompanyId;
        for (const id of ids) {
          try {
            const found = await fetchDocs(id);
            if (found.length > 0) {
              return { docs: found, usedId: id };
            }
            if (!docs.length) {
              docs = found;
              usedId = id;
            }
          } catch (e) {
            console.error(e);
          }
        }
        return { docs, usedId };
      };

      let { docs, usedId } = await tryIds(ordered);

      if (docs.length === 0) {
        try {
          const { data: my } = await callAPI("/company/my", {}, "GET");
          const before = ordered.length;
          (my?.companies || []).forEach((c) => add(c.id));
          const extra = ordered.slice(before);
          if (extra.length) {
            const again = await tryIds(extra);
            if (again.docs.length > 0) {
              docs = again.docs;
              usedId = again.usedId;
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      setDocuments(docs);
      loadedCompanyRef.current = usedId || routeCompanyId;
      if (usedId && usedId !== routeCompanyId) {
        props.history.replace(`/company-documents/${usedId}`);
      }
    } catch (e) {
      console.error(e);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [routeCompanyId, selectedCompanyId, props.history]);

  useEffect(() => {
    if (!routeCompanyId) return;
    const silent = !!(loadedCompanyRef.current && loadedCompanyRef.current === routeCompanyId && documents.length > 0);
    load(silent);
  }, [load, routeCompanyId]);

  const openDoc = (item) => {
    setViewDoc(item);
  };

  const downloadDoc = async (item) => {
    if (!item) return;
    setDownloadingId(item.id);
    try {
      const url = BASE_URL + item.fileUrl;
      const response = await axios({
        url,
        method: "GET",
        responseType: "blob",
      });
      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute(
        "download",
        item.attachedFileName || item.title || "document"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error(e);
    } finally {
      setDownloadingId(null);
    }
  };

  const viewLabel = strings.preview || strings.view || "Ansehen";
  const downloadLabel = strings.download_selected || "Download";

  return (
    <div className="company-documents">
      <div className={`company-documents__column${isDesktop ? " company-documents__column--desktop" : ""}`}>
        <div className="company-documents__header">
          <h1 className="company-documents__title">
            {strings.company_documents || "Firmendokumente"}
          </h1>
        </div>

        <div className="company-documents__body">
          {loading ? (
            <div className="company-docs-skeleton" aria-busy="true" aria-live="polite">
              {[1, 2, 3].map((i) => (
                <div key={i} className="company-docs-skeleton-row">
                  <div className="company-documents__grow">
                    <div
                      className={`skeleton company-documents__sk-title${i === 2 ? " company-documents__sk-title--short" : ""}`}
                    />
                    <div
                      className="skeleton company-documents__sk-meta"
                    />
                  </div>
                  <div className="skeleton skeleton-circle company-documents__sk-icon" />
                  <div className="skeleton skeleton-circle company-documents__sk-icon" />
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <p
              className="company-docs-list-enter company-documents__empty"
            >
              {strings.company_documents_empty || "Keine Dokumente vorhanden."}
            </p>
          ) : (
            <ul
              className="company-docs-list-enter company-documents__list"
            >
              {documents.map((item) => (
                <li
                  key={item.id}
                  className="company-documents__item"
                >
                  <div className="company-documents__grow">
                    <div className="company-documents__item-title">
                      {item.title || item.attachedFileName}
                    </div>
                    <div className="company-documents__item-meta">
                      {formatBytes(item.fileSize)}
                      {item.createdAt
                        ? ` · ${new Date(item.createdAt).toLocaleDateString()}`
                        : ""}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openDoc(item)}
                    title={viewLabel}
                    aria-label={viewLabel}
                    className="kj-btn kj-btn--icon kj-btn--internal"
                  >
                    <HiOutlineEye size={22} />
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDoc(item)}
                    disabled={downloadingId === item.id}
                    title={downloadLabel}
                    aria-label={downloadLabel}
                    className="kj-btn kj-btn--icon kj-btn--internal"
                  >
                    <HiDownload size={20} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {isDesktop && <div className="kj-flex-1" />}

      {viewDoc && (
        <CompanyDocumentViewerModal
          fileUrl={viewDoc.fileUrl}
          fileName={viewDoc.attachedFileName || viewDoc.title || "document"}
          onClose={() => setViewDoc(null)}
          onDownload={() => downloadDoc(viewDoc)}
          downloadLabel={downloadLabel}
        />
      )}
    </div>
  );
}

export default withRouter(CompanyDocuments);
